import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { CalendarIcon, Pause, Search, X } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Calendar } from '../../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Progress } from '../../../components/ui/progress';
import { Textarea } from '../../../components/ui/textarea';

import { parseUrlList, parsePages } from '../lib/parse-instagram-url';
import { extractLinks } from '../lib/extract-links';

import type { BatchProgress } from '../types/statistics';
import type { DateRange } from 'react-day-picker';

type LinkInputFormProps = {
  onSubmit: (urls: string[], dateFrom?: string, dateTo?: string, pages?: string[][][]) => void;
  onCancel: () => void;
  isPending: boolean;
  isPaused: boolean;
  pauseReason: string | null;
  progress: BatchProgress | null;
  elapsedSeconds: number;
  displayPercent: number;
  /** Текст ссылок хранит стор вкладки: возврат на неё не стирает вставленное. */
  text: string;
  onTextChange: (text: string) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
};

/** yyyy-MM-dd по местному времени: toISOString у полуночи UTC+5 давал вчерашний день. */
function toDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatElapsed(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs} сек`;
  return `${mins} мин ${secs} сек`;
}

export function LinkInputForm({
  onSubmit,
  onCancel,
  isPending,
  isPaused,
  pauseReason,
  progress,
  elapsedSeconds,
  displayPercent,
  text,
  onTextChange: setText,
  dateRange,
  onDateRangeChange,
}: LinkInputFormProps): JSX.Element {
  const [validationError, setValidationError] = useState<string | null>(null);

  const parsed = useMemo(() => {
    if (!text.trim()) return { valid: [], invalid: [], duplicates: [] };
    return parseUrlList(text);
  }, [text]);

  function handleSubmit(): void {
    if (parsed.valid.length === 0) {
      setValidationError('Вставьте хотя бы одну корректную ссылку на Instagram пост или рилс');
      return;
    }

    setValidationError(null);
    const pages = parsePages(text);
    const urls = [...new Set(pages.flat().flat())];
    const dateFrom = dateRange?.from ? toDay(dateRange.from) : undefined;
    const dateTo = dateRange?.to ? toDay(dateRange.to) : undefined;

    onSubmit(urls, dateFrom, dateTo, pages);
  }

  function formatDateRange(): string {
    if (!dateRange?.from) return 'Выбрать период';
    if (!dateRange.to) return format(dateRange.from, 'd MMM yyyy', { locale: ru });
    return `${format(dateRange.from, 'd MMM yyyy', { locale: ru })} — ${format(dateRange.to, 'd MMM yyyy', { locale: ru })}`;
  }

  function formatProgressText(): string {
    if (!progress) return '';
    const { completedUrls, totalUrls, currentBatch, totalBatches } = progress;
    const elapsed = formatElapsed(elapsedSeconds);
    if (totalBatches <= 1) {
      return `Обработка ${totalUrls} ссылок... ${elapsed}`;
    }
    return `Батч ${currentBatch} из ${totalBatches} — ${completedUrls} из ${totalUrls} ссылок · ${elapsed}`;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Textarea
          placeholder={
            'Вставьте ссылки на посты, рилсы или TikTok-видео (одна на строку):\nhttps://www.instagram.com/reel/DU5TLq2iEci/\nhttps://www.tiktok.com/@tokayev.news/video/7662372372757974292'
          }
          value={text}
          onChange={e => {
            setText(e.target.value);
            setValidationError(null);
          }}
          onPaste={e => {
            // Ссылки копируют вместе с заголовками тем и подписями — оставляем
            // только сами ссылки, чтобы не вычищать список руками.
            const pasted = e.clipboardData.getData('text');
            const cleaned = extractLinks(pasted);

            // Диагностика в консоль: что выброшено и уцелели ли пустые строки —
            // по ним делятся страницы отчёта (одна пустая = группа, две = страница).
            const droppedLines = pasted
              .split('\n')
              .filter(l => l.trim() && !/https?:\/\//.test(l)).length;
            const gaps = (cleaned.match(/\n{2,}/g) ?? []).map(g => g.length - 1);
            const pages = parsePages(cleaned);
            console.info(
              `[Ссылки] ${cleaned === pasted ? 'вставка без изменений' : 'вставка очищена'}: ` +
                `ссылок ${cleaned.split('\n').filter(l => l.trim()).length}, ` +
                `убрано строк текста ${droppedLines}, ` +
                `пустых строк между ссылками: ${gaps.join(',') || 'нет'} → ` +
                `страниц ${pages.length}, групп ${pages.flat().length}`,
            );

            if (cleaned === pasted) return;
            e.preventDefault();
            const el = e.currentTarget;
            const before = text.slice(0, el.selectionStart ?? text.length);
            const after = text.slice(el.selectionEnd ?? text.length);
            const glue = before && !before.endsWith('\n') ? '\n' : '';
            setText(`${before}${glue}${cleaned}${after}`);
            setValidationError(null);
          }}
          rows={6}
          // Ссылок бывает много: даём тянуть поле за уголок по вертикали.
          // resize-y перебивает resize-none из базового Textarea, а потолок
          // высоты снят — иначе растягивание упиралось бы в max-h.
          className="resize-y font-mono text-sm min-h-[160px] overflow-y-auto"
          disabled={isPending}
        />
      </div>

      {parsed.valid.length > 0 && !isPending && (
        <div className="flex flex-col gap-1 text-xs">
          <p className="text-muted-foreground">
            {parsed.valid.length}{' '}
            {parsed.valid.length === 1 ? 'ссылка' : parsed.valid.length < 5 ? 'ссылки' : 'ссылок'}{' '}
            вставлено
          </p>
          {parsed.duplicates.length > 0 && (
            <details className="text-amber-600">
              <summary className="cursor-pointer hover:underline">
                {parsed.duplicates.length}{' '}
                {parsed.duplicates.length === 1
                  ? 'дубликат'
                  : parsed.duplicates.length < 5
                    ? 'дубликата'
                    : 'дубликатов'}{' '}
                пропущено
              </summary>
              <ul className="mt-1 ml-4 list-disc text-xs text-amber-700 max-h-[120px] overflow-y-auto">
                {parsed.duplicates.map(url => (
                  <li key={url} className="truncate">
                    {url}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {parsed.invalid.length > 0 && (
            <details className="text-destructive">
              <summary className="cursor-pointer hover:underline">
                {parsed.invalid.length}{' '}
                {parsed.invalid.length === 1
                  ? 'некорректная ссылка'
                  : parsed.invalid.length < 5
                    ? 'некорректные ссылки'
                    : 'некорректных ссылок'}
              </summary>
              <ul className="mt-1 ml-4 list-disc text-xs max-h-[120px] overflow-y-auto">
                {parsed.invalid.map(line => (
                  <li key={line} className="truncate">
                    {line}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {validationError && <p className="text-sm text-destructive">{validationError}</p>}

      <div className="flex items-center gap-2 flex-wrap">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2" disabled={isPending}>
              <CalendarIcon className="size-4" />
              {formatDateRange()}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={onDateRangeChange}
              locale={ru}
              numberOfMonths={2}
            />
            {dateRange && (
              <div className="p-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => onDateRangeChange(undefined)}
                >
                  Сбросить период
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {isPending ? (
          <Button onClick={onCancel} variant="destructive" className="gap-2">
            <X className="size-4" />
            Отменить
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={!text.trim()} className="gap-2">
            <Search className="size-4" />
            Получить статистику
          </Button>
        )}
      </div>

      {isPending && progress && (
        <div className="flex flex-col gap-1.5">
          <Progress value={displayPercent} className="h-2" />
          {isPaused && pauseReason ? (
            <div className="flex items-center gap-1.5 text-xs text-amber-600">
              <Pause className="size-3" />
              <span>{pauseReason}</span>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{formatProgressText()}</p>
          )}
        </div>
      )}
    </div>
  );
}
